import { Decimal } from 'decimal.js';
export var ParseNode;
(function (ParseNode) {
    let Type;
    (function (Type) {
        Type["Identifier"] = "Identifier";
        Type["AccessIdentifier"] = "AccessIdentifier";
        Type["NullLiteral"] = "NullLiteral";
        Type["BooleanLiteral"] = "BooleanLiteral";
        Type["NumberLiteral"] = "NumberLiteral";
        Type["HexLiteral"] = "HexLiteral";
        Type["StringLiteral"] = "StringLiteral";
        Type["RegExpLiteral"] = "RegExpLiteral";
        Type["DateLiteral"] = "DateLiteral";
        Type["UnaryExpression"] = "UnaryExpression";
        Type["BinaryExpression"] = "BinaryExpression";
        Type["LogicalExpression"] = "LogicalExpression";
        Type["AccessExpression"] = "AccessExpression";
        Type["CaseExpression"] = "CaseExpression";
        Type["CaseWhen"] = "CaseWhen";
        Type["CaseElse"] = "CaseElse";
        Type["IfExpression"] = "IfExpression";
        Type["IfBase"] = "IfBase";
        Type["ElseIf"] = "ElseIf";
        Type["IfElse"] = "IfElse";
        Type["CallExpression"] = "CallExpression";
    })(Type = ParseNode.Type || (ParseNode.Type = {}));
    function id(node) {
        return node.location.start.offset;
    }
    ParseNode.id = id;
    function isAccess(node) {
        return (node.type === "AccessExpression" ||
            node.type === "AccessIdentifier" ||
            node.type === "Identifier");
    }
    ParseNode.isAccess = isAccess;
    function numberToString(sign, n, radix) {
        const absValue = Decimal.abs(n).toNumber();
        const absValueString = absValue.toString(radix);
        switch (radix) {
            case 2:
                return sign + '0b' + absValueString;
            case 8:
                return sign + '0o' + absValueString;
            case 16:
                return sign + '0x' + absValueString;
            default:
                return sign + absValueString;
        }
    }
    ParseNode.numberToString = numberToString;
    function toString(node) {
        switch (node.type) {
            case "NullLiteral":
                return 'NULL';
            case "BooleanLiteral":
                return node.value === true ? 'TRUE' : 'FALSE';
            case "HexLiteral":
                return numberToString(node.sign, node.value, 16);
            case "NumberLiteral":
                return numberToString(node.sign, node.value, 10);
            case "StringLiteral":
                return `"${node.value}"`;
            case "DateLiteral":
                return node.value.toString();
            case "RegExpLiteral":
                return `"${node.value.source}"`;
            case "Identifier":
                return node.name;
            case "AccessIdentifier":
                return `[${node.name}]`;
            case "AccessExpression": {
                return `${toString(node.object)}.${toString(node.member)}`;
            }
            case "CallExpression": {
                const argStrings = node.arguments.map((arg) => toString(arg));
                return `${node.callee}(${argStrings.join(', ')})`;
            }
            case "UnaryExpression": {
                return `${node.operator}(${toString(node.operand)})`;
            }
            case "BinaryExpression": {
                return `(${toString(node.left)} ${node.operator} ${toString(node.right)})`;
            }
            case "LogicalExpression": {
                return `(${toString(node.left)} ${node.operator} ${toString(node.right)})`;
            }
            case "CaseExpression": {
                const value = toString(node.value);
                const whens = node.cases.map((when) => toString(when));
                if (node.else == null) {
                    return `CASE ${value} ${whens.join(' ')} END`;
                }
                else {
                    return `CASE ${value} ${whens.join(' ')} ${toString(node.else)} END`;
                }
            }
            case "CaseWhen": {
                return `WHEN ${toString(node.value)} THEN ${toString(node.result)}`;
            }
            case "CaseElse": {
                return `ELSE ${toString(node.result)}`;
            }
            case "IfExpression": {
                const base = toString(node.base);
                const elses = node.elses.map((elseif) => toString(elseif));
                if (node.else == null) {
                    return `IF ${base} ${elses.join(' ')} END`;
                }
                else {
                    return `IF ${base} ${elses.join(' ')} ${toString(node.else)} END`;
                }
            }
            case "IfBase": {
                return `${toString(node.condition)} THEN ${toString(node.result)}`;
            }
            case "ElseIf": {
                return `ELSEIF ${toString(node.condition)} THEN ${toString(node.result)}`;
            }
            case "IfElse": {
                return `ELSE ${toString(node.result)}`;
            }
        }
    }
    ParseNode.toString = toString;
    function toJson(node) {
        switch (node.type) {
            case "HexLiteral":
                return {
                    ...node,
                    value: numberToString(node.sign, node.value, 16),
                };
            case "NumberLiteral":
                return {
                    ...node,
                    value: numberToString(node.sign, node.value, 10),
                };
            case "DateLiteral":
                return { ...node, value: node.value.toString() };
            case "AccessExpression": {
                if (node.member == null) {
                    return { ...node };
                }
                else {
                    return { ...node, member: toJson(node.member) };
                }
            }
            case "CallExpression": {
                return { ...node, arguments: node.arguments.map((arg) => toJson(arg)) };
            }
            case "UnaryExpression": {
                const { operator } = node;
                return { ...node, operator, operand: toJson(node.operand) };
            }
            case "BinaryExpression": {
                return { ...node, left: toJson(node.left), right: toJson(node.right) };
            }
            case "LogicalExpression": {
                return { ...node, left: toJson(node.left), right: toJson(node.right) };
            }
            case "CaseExpression": {
                const value = toJson(node.value);
                const whens = node.cases.map((when) => toJson(when));
                if (node.else == null) {
                    return { ...node, value, whens };
                }
                else {
                    return { ...node, value, whens, else: toJson(node.else) };
                }
            }
            case "CaseWhen": {
                return {
                    ...node,
                    value: toJson(node.value),
                    result: toJson(node.result),
                };
            }
            case "CaseElse": {
                return { ...node, result: toJson(node.result) };
            }
            case "IfExpression": {
                const base = toString(node.base);
                const elses = node.elses.map((elseif) => toString(elseif));
                if (node.else == null) {
                    return { ...node, base, elses };
                }
                else {
                    return { ...node, base, elses, else: toJson(node.else) };
                }
            }
            case "IfBase": {
                return {
                    ...node,
                    condition: toJson(node.condition),
                    result: toJson(node.result),
                };
            }
            case "ElseIf": {
                return {
                    ...node,
                    condition: toJson(node.condition),
                    result: toJson(node.result),
                };
            }
            case "IfElse": {
                return { ...node, result: toJson(node.result) };
            }
            default:
                return { ...node };
        }
    }
    ParseNode.toJson = toJson;
})(ParseNode || (ParseNode = {}));
